import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useState } from 'react';

import type { ReactNode } from 'react';

/**
 * A list whose rows are dragged into a different order.
 *
 * Native drag events rather than a library — GEN-45 made that call for the scene arc and
 * GEN-59 is the second list to want it. This is one desktop console and neither list runs
 * past a couple of dozen rows, so `@dnd-kit` would be a dependency and a provider for
 * something the platform already does.
 *
 * Each row owns its drag handlers and the caller owns the order, so a row cannot disagree
 * with the list about where it is.
 */
export function DragList({
  children,
  onMove,
  disabled,
}: {
  /** One node per row, in order. Dragging reorders these. */
  children: ReactNode[];
  onMove: (from: number, to: number) => void;
  disabled: boolean;
}) {
  const [dragging, setDragging] = useState<number>();
  const [over, setOver] = useState<number>();

  return (
    <Stack spacing={1}>
      {children.map((child, index) => (
        <Box
          // The index is the identity here: these are positions in an order, and the
          // row's own key lives on the child the caller passed.
          key={index}
          data-testid="drag-row"
          draggable={!disabled}
          onDragStart={() => {
            setDragging(index);
          }}
          onDragEnd={() => {
            setDragging(undefined);
            setOver(undefined);
          }}
          onDragOver={(event) => {
            // Without this the drop never fires: the default is to refuse.
            event.preventDefault();
            setOver(index);
          }}
          onDrop={() => {
            if (dragging !== undefined && dragging !== index)
              onMove(dragging, index);

            setDragging(undefined);
            setOver(undefined);
          }}
          sx={{
            cursor: disabled ? 'default' : 'grab',
            opacity: dragging === index ? 0.4 : 1,
            borderTop: 2,
            borderColor:
              over === index && dragging !== index
                ? 'primary.main'
                : 'transparent',
          }}
        >
          {child}
        </Box>
      ))}
    </Stack>
  );
}
